import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import api from '../../lib/axios';
import AudioBriefRecorder from './AudioBriefRecorder';

// Brief templates by project type
const briefTemplates = {
  logo: {
    title: 'Brief Création de Logo',
    sections: [
      {
        title: 'Informations générales',
        fields: [
          { name: 'company_name', label: 'Nom de l\'entreprise', type: 'text', required: true },
          { name: 'activity', label: 'Secteur d\'activité', type: 'text', required: true },
          { name: 'target_audience', label: 'Cible / Public visé', type: 'textarea', required: true },
          { name: 'company_values', label: 'Valeurs de l\'entreprise', type: 'textarea', required: true },
        ]
      },
      {
        title: 'Direction artistique',
        fields: [
          { name: 'style', label: 'Style souhaité', type: 'select', options: ['Moderne', 'Classique', 'Minimaliste', 'Ludique', 'Premium', 'Autre'], required: true },
          { name: 'colors', label: 'Couleurs souhaitées', type: 'text', placeholder: 'Ex: Bleu marine, Or' },
          { name: 'avoid_colors', label: 'Couleurs à éviter', type: 'text' },
          { name: 'inspirations', label: 'Logos inspirants (liens ou descriptions)', type: 'textarea' },
          { name: 'competitors', label: 'Logos de concurrents à éviter', type: 'textarea' },
        ]
      },
      {
        title: 'Utilisation',
        fields: [
          { name: 'usage_web', label: 'Site web', type: 'checkbox' },
          { name: 'usage_print', label: 'Impression (cartes, flyers)', type: 'checkbox' },
          { name: 'usage_signage', label: 'Signalétique / Enseigne', type: 'checkbox' },
          { name: 'usage_vehicle', label: 'Véhicules', type: 'checkbox' },
          { name: 'usage_clothing', label: 'Vêtements / Textile', type: 'checkbox' },
          { name: 'special_requirements', label: 'Exigences particulières', type: 'textarea' },
        ]
      }
    ]
  },
  visual: {
    title: 'Brief Création Visuelle',
    sections: [
      {
        title: 'Contexte',
        fields: [
          { name: 'project_context', label: 'Contexte du projet', type: 'textarea', required: true },
          { name: 'objective', label: 'Objectif de la création', type: 'textarea', required: true },
          { name: 'target_audience', label: 'Public cible', type: 'textarea', required: true },
        ]
      },
      {
        title: 'Spécifications techniques',
        fields: [
          { name: 'format', label: 'Format(s) souhaité(s)', type: 'select', options: ['Affiche A4', 'Affiche A3', 'Bannière web', 'Post réseaux sociaux', 'Flyer', 'Autre'], required: true },
          { name: 'dimensions', label: 'Dimensions spécifiques', type: 'text', placeholder: 'Ex: 1920x1080px' },
          { name: 'quantity', label: 'Nombre de visuels', type: 'number', required: true },
        ]
      },
      {
        title: 'Direction créative',
        fields: [
          { name: 'style', label: 'Style graphique', type: 'select', options: ['Photo-réaliste', 'Illustration', 'Minimaliste', 'Corporate', 'Créatif', 'Autre'] },
          { name: 'mood', label: 'Ambiance / Mood', type: 'textarea' },
          { name: 'mandatory_elements', label: 'Éléments obligatoires', type: 'textarea', placeholder: 'Logo, texte, images...' },
          { name: 'text_content', label: 'Texte à intégrer', type: 'textarea' },
        ]
      }
    ]
  },
  video: {
    title: 'Brief Production Vidéo',
    sections: [
      {
        title: 'Informations projet',
        fields: [
          { name: 'video_type', label: 'Type de vidéo', type: 'select', options: ['Corporate', 'Publicitaire', 'Tutoriel', 'Interview', 'Événementiel', 'Animation', 'Autre'], required: true },
          { name: 'objective', label: 'Objectif de la vidéo', type: 'textarea', required: true },
          { name: 'target_audience', label: 'Public cible', type: 'textarea', required: true },
          { name: 'key_message', label: 'Message clé à faire passer', type: 'textarea', required: true },
        ]
      },
      {
        title: 'Spécifications techniques',
        fields: [
          { name: 'duration', label: 'Durée souhaitée', type: 'select', options: ['15 secondes', '30 secondes', '1 minute', '2-3 minutes', '5+ minutes'], required: true },
          { name: 'format', label: 'Format de diffusion', type: 'select', options: ['16:9 (Horizontal)', '9:16 (Vertical/Stories)', '1:1 (Carré)', 'Multiple'], required: true },
          { name: 'platforms', label: 'Plateformes de diffusion', type: 'textarea', placeholder: 'YouTube, Instagram, Site web...' },
        ]
      },
      {
        title: 'Contenu',
        fields: [
          { name: 'script_provided', label: 'Script fourni', type: 'checkbox' },
          { name: 'voiceover', label: 'Voix off souhaitée', type: 'checkbox' },
          { name: 'music', label: 'Musique/Sound design', type: 'select', options: ['Fournie par le client', 'À rechercher (libre de droits)', 'Création originale'] },
          { name: 'footage', label: 'Images/Vidéos existantes à utiliser', type: 'textarea' },
          { name: 'references', label: 'Vidéos de référence (liens)', type: 'textarea' },
        ]
      }
    ]
  },
  website: {
    title: 'Brief Site Internet',
    sections: [
      {
        title: 'Informations générales',
        fields: [
          { name: 'site_type', label: 'Type de site', type: 'select', options: ['Vitrine', 'E-commerce', 'Blog', 'Portfolio', 'Application web', 'Landing page'], required: true },
          { name: 'objective', label: 'Objectif principal du site', type: 'textarea', required: true },
          { name: 'target_audience', label: 'Public cible', type: 'textarea', required: true },
          { name: 'existing_site', label: 'URL du site actuel (si refonte)', type: 'text' },
        ]
      },
      {
        title: 'Structure & Contenu',
        fields: [
          { name: 'pages', label: 'Pages souhaitées', type: 'textarea', placeholder: 'Accueil, À propos, Services, Contact...', required: true },
          { name: 'features', label: 'Fonctionnalités spécifiques', type: 'textarea', placeholder: 'Formulaire, Blog, Espace membre...' },
          { name: 'content_provided', label: 'Contenu fourni (textes, images)', type: 'checkbox' },
          { name: 'multilingual', label: 'Site multilingue', type: 'checkbox' },
        ]
      },
      {
        title: 'Design & Technique',
        fields: [
          { name: 'design_style', label: 'Style de design', type: 'select', options: ['Moderne', 'Minimaliste', 'Corporate', 'Créatif', 'E-commerce'] },
          { name: 'inspirations', label: 'Sites d\'inspiration', type: 'textarea' },
          { name: 'hosting', label: 'Hébergement souhaité', type: 'select', options: ['À définir', 'Hébergement existant', 'Nouveau hébergement'] },
          { name: 'domain', label: 'Nom de domaine', type: 'text', placeholder: 'www.exemple.com' },
          { name: 'seo', label: 'Optimisation SEO souhaitée', type: 'checkbox' },
        ]
      }
    ]
  },
  mobile_app: {
    title: 'Brief Application Mobile',
    sections: [
      {
        title: 'Vision du projet',
        fields: [
          { name: 'app_concept', label: 'Concept de l\'application', type: 'textarea', required: true },
          { name: 'problem_solved', label: 'Problème résolu', type: 'textarea', required: true },
          { name: 'target_users', label: 'Utilisateurs cibles', type: 'textarea', required: true },
          { name: 'competitors', label: 'Applications concurrentes', type: 'textarea' },
        ]
      },
      {
        title: 'Spécifications',
        fields: [
          { name: 'platforms', label: 'Plateformes', type: 'select', options: ['iOS uniquement', 'Android uniquement', 'iOS + Android'], required: true },
          { name: 'core_features', label: 'Fonctionnalités principales', type: 'textarea', required: true },
          { name: 'user_accounts', label: 'Comptes utilisateurs', type: 'checkbox' },
          { name: 'payments', label: 'Paiements in-app', type: 'checkbox' },
          { name: 'notifications', label: 'Notifications push', type: 'checkbox' },
          { name: 'offline_mode', label: 'Mode hors-ligne', type: 'checkbox' },
        ]
      },
      {
        title: 'Design & UX',
        fields: [
          { name: 'design_style', label: 'Style de design', type: 'select', options: ['iOS natif', 'Material Design', 'Custom', 'Minimaliste'] },
          { name: 'branding', label: 'Charte graphique existante', type: 'checkbox' },
          { name: 'inspirations', label: 'Applications d\'inspiration', type: 'textarea' },
        ]
      }
    ]
  }
};

const BriefForm = ({ projectId, projectType, onSuccess, onCancel, theme }) => {
  const template = briefTemplates[projectType] || briefTemplates.logo;
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [audioTranscription, setAudioTranscription] = useState('');

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleAudioTranscription = (transcription) => {
    setAudioTranscription(transcription);
    // Also add to formData for saving
    setFormData(prev => ({ ...prev, audio_transcription: transcription }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    // Convert form data to brief fields format
    const fields = [];
    template.sections.forEach(section => {
      section.fields.forEach(field => {
        fields.push({
          field_name: field.name,
          field_type: field.type,
          label: field.label,
          required: field.required || false,
          options: field.options || null,
          value: formData[field.name] || (field.type === 'checkbox' ? false : '')
        });
      });
    });

    // Add audio transcription as a field if present
    if (audioTranscription) {
      fields.push({
        field_name: 'audio_transcription',
        field_type: 'textarea',
        label: 'Transcription Audio',
        required: false,
        options: null,
        value: audioTranscription
      });
    }

    try {
      await api.post('/briefs/', {
        project_id: projectId,
        project_type: projectType,
        fields: fields
      });
      toast.success('Brief créé avec succès !');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du brief');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field) => {
    switch (field.type) {
      case 'text':
      case 'number':
        return (
          <Input
            type={field.type}
            value={formData[field.name] || ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            className="input-default"
            required={field.required}
          />
        );
      case 'textarea':
        return (
          <Textarea
            value={formData[field.name] || ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            className="input-default min-h-[80px]"
            required={field.required}
          />
        );
      case 'select':
        return (
          <Select
            value={formData[field.name] || ''}
            onValueChange={(v) => handleFieldChange(field.name, v)}
          >
            <SelectTrigger className="input-default">
              <SelectValue placeholder="Sélectionner..." />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map(opt => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'checkbox':
        return (
          <div className="flex items-center space-x-2">
            <Checkbox
              id={field.name}
              checked={formData[field.name] || false}
              onCheckedChange={(checked) => handleFieldChange(field.name, checked)}
            />
            <label htmlFor={field.name} className="text-sm cursor-pointer">
              {field.label}
            </label>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold" style={{ color: theme?.colors?.primary }}>
            {template.title}
          </h3>
          <p className="text-sm text-[#64748b]">
            Remplissez le cahier des charges pour ce projet
          </p>
        </div>
        <Badge style={{ backgroundColor: `${theme?.colors?.accent}20`, color: theme?.colors?.accent }}>
          {projectType}
        </Badge>
      </div>

      {/* Audio Brief Recorder Section */}
      <AudioBriefRecorder 
        onTranscription={handleAudioTranscription}
        theme={theme}
      />

      {template.sections.map((section, sectionIndex) => (
        <Card key={sectionIndex} className="border border-[#e2e8f0]">
          <CardHeader className="pb-3">
            <CardTitle className="text-base" style={{ color: theme?.colors?.primary }}>
              {section.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {section.fields.map((field) => (
              <div key={field.name} className={field.type === 'checkbox' ? '' : 'space-y-2'}>
                {field.type !== 'checkbox' && (
                  <Label className="flex items-center gap-1">
                    {field.label}
                    {field.required && <span className="text-red-500">*</span>}
                  </Label>
                )}
                {renderField(field)}
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annuler
        </Button>
        <Button 
          type="submit" 
          disabled={saving}
          style={{ backgroundColor: theme?.colors?.accent }}
        >
          {saving ? 'Création...' : 'Créer le brief'}
        </Button>
      </div>
    </form>
  );
};

export default BriefForm;
